import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/ReportProblemOutlined';
import PrimaryButton from 'common/components/buttons/primaryButton';
import LoadingProgress from 'common/components/progress/loading';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const STRATEGIES = [
  {
    value: 'end',
    label: 'Encerrar as atividades',
    help: 'A data de término passa a ser agora. Elas somem do app, mas o histórico de participação é mantido.',
  },
  {
    value: 'move',
    label: 'Mover para outra categoria',
    help: 'As atividades continuam ativas, apenas passam a ser exibidas na categoria escolhida.',
  },
  {
    value: 'deactivate',
    label: 'Desativar as atividades',
    help: 'As atividades ficam inativas e deixam de ser exibidas, mas podem ser reativadas depois.',
  },
];

const pluralize = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

/**
 * Fluxo guiado de exclusão de categoria.
 *
 * Passo 1 — avisos prévios: mostra o que será afetado e, quando alguma atividade
 * ficaria sem categoria nenhuma, exige a escolha de uma estratégia.
 * Passo 2 — confirmação: resume em texto exatamente o que será executado.
 */
export default function DeleteCategoryDialog(props) {
  const { open, category, usage, usageLoading, deleteLoading, categories, onClose, onConfirm } =
    props;

  const [step, setStep] = useState('warnings');
  const [strategy, setStrategy] = useState('');
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setStep('warnings');
      setStrategy('');
      setTargetCategoryId('');
      setReason('');
    }
  }, [open, category?.id]);

  const counts = usage?.counts;
  const needsStrategy = !!counts && counts.exclusive > 0;
  const targetOptions = (categories || []).filter(
    (item) => item.id !== category?.id && item.station_id
  );

  const chosenStrategy = needsStrategy ? strategy : 'unlink';
  const targetCategory = targetOptions.find((item) => item.id === targetCategoryId);

  const canContinue =
    !!counts && (!needsStrategy || (!!strategy && (strategy !== 'move' || !!targetCategoryId)));

  function handleConfirm() {
    onConfirm({
      strategy: chosenStrategy,
      target_category_id: chosenStrategy === 'move' ? targetCategoryId : null,
      reason: reason.trim() || null,
    });
  }

  function renderSummaryLine() {
    if (counts.total === 0) {
      return (
        <Typography variant="body2">
          Nenhuma atividade está vinculada a esta categoria no momento.
        </Typography>
      );
    }
    return (
      <>
        <Typography variant="body2" gutterBottom>
          <strong>{pluralize(counts.total, 'atividade', 'atividades')}</strong>{' '}
          {counts.total === 1 ? 'está vinculada' : 'estão vinculadas'} a esta categoria.
        </Typography>
        {counts.shared > 0 ? (
          <Typography variant="body2" gutterBottom>
            • {pluralize(counts.shared, 'atividade também pertence', 'atividades também pertencem')}{' '}
            a outra categoria — {counts.shared === 1 ? 'ela' : 'elas'} apenas{' '}
            {counts.shared === 1 ? 'perde o vínculo' : 'perdem o vínculo'} com esta e{' '}
            {counts.shared === 1 ? 'continua' : 'continuam'} no ar.
          </Typography>
        ) : null}
        {counts.exclusive > 0 ? (
          <Typography variant="body2" gutterBottom>
            • {pluralize(counts.exclusive, 'atividade ficaria', 'atividades ficariam')} sem nenhuma
            categoria
            {counts.running > 0
              ? ` (${pluralize(counts.running, 'está em andamento', 'estão em andamento')})`
              : ''}
            . Escolha abaixo o que fazer com {counts.exclusive === 1 ? 'ela' : 'elas'}.
          </Typography>
        ) : null}
      </>
    );
  }

  function renderAffectedList() {
    if (!counts || counts.exclusive === 0) return null;
    return (
      <Box mt={1} mb={2} pl={1} style={{ maxHeight: 140, overflowY: 'auto' }}>
        {usage.exclusive.map((action) => (
          <Typography key={action.id} variant="caption" component="div" color="textSecondary">
            — {action.name} ({action.status})
          </Typography>
        ))}
      </Box>
    );
  }

  function renderWarningsStep() {
    return (
      <>
        <Box display="flex" alignItems="flex-start" mb={2}>
          <WarningIcon style={{ color: '#E9B44C', marginRight: 8 }} />
          <Box>{renderSummaryLine()}</Box>
        </Box>

        {renderAffectedList()}

        {needsStrategy ? (
          <>
            <Divider />
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                O que fazer com{' '}
                {counts.exclusive === 1 ? 'a atividade que ficaria' : 'as atividades que ficariam'}{' '}
                sem categoria?
              </Typography>
              <RadioGroup value={strategy} onChange={(e) => setStrategy(e.target.value)}>
                {STRATEGIES.map((option) => (
                  <Box key={option.value} mb={1}>
                    <FormControlLabel
                      value={option.value}
                      control={<Radio color="primary" />}
                      label={option.label}
                      disabled={option.value === 'move' && targetOptions.length === 0}
                    />
                    <Typography
                      variant="caption"
                      component="div"
                      color="textSecondary"
                      style={{ marginLeft: 32 }}>
                      {option.value === 'move' && targetOptions.length === 0
                        ? 'Você não tem outra categoria personalizada para receber estas atividades.'
                        : option.help}
                    </Typography>
                  </Box>
                ))}
              </RadioGroup>

              {strategy === 'move' ? (
                <FormControl variant="outlined" fullWidth margin="dense">
                  <InputLabel id="target-category-label">Categoria de destino</InputLabel>
                  <Select
                    labelId="target-category-label"
                    label="Categoria de destino"
                    value={targetCategoryId}
                    onChange={(e) => setTargetCategoryId(e.target.value)}>
                    {targetOptions.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Box>
          </>
        ) : null}

        <Box mt={2}>
          <TextField
            id="delete-category-reason"
            variant="outlined"
            fullWidth
            margin="dense"
            label="Motivo da exclusão (opcional)"
            helperText="Fica registrado junto com a categoria para consulta futura."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        </Box>
      </>
    );
  }

  function renderConfirmStep() {
    const lines = [];

    if (counts.shared > 0) {
      lines.push(
        `${pluralize(
          counts.shared,
          'atividade perde',
          'atividades perdem'
        )} o vínculo com esta categoria e ${
          counts.shared === 1 ? 'continua' : 'continuam'
        } nas demais.`
      );
    }
    if (counts.exclusive > 0) {
      if (chosenStrategy === 'end') {
        lines.push(
          `${pluralize(
            counts.exclusive,
            'atividade será encerrada',
            'atividades serão encerradas'
          )} agora.`
        );
      } else if (chosenStrategy === 'deactivate') {
        lines.push(
          `${pluralize(
            counts.exclusive,
            'atividade será desativada',
            'atividades serão desativadas'
          )}.`
        );
      } else if (chosenStrategy === 'move') {
        lines.push(
          `${pluralize(
            counts.exclusive,
            'atividade será movida',
            'atividades serão movidas'
          )} para "${targetCategory?.name}".`
        );
      }
    }
    lines.push(
      'A categoria deixa de aparecer no painel e no app, mas continua no banco de dados marcada como excluída, com autor e data.'
    );

    return (
      <>
        <Typography variant="body2" gutterBottom>
          Ao confirmar a exclusão de <strong>{category?.name}</strong>:
        </Typography>
        <Box mt={1}>
          {lines.map((line, index) => (
            <Typography key={index} variant="body2" gutterBottom>
              • {line}
            </Typography>
          ))}
        </Box>
        {reason.trim() ? (
          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              Motivo registrado: {reason.trim()}
            </Typography>
          </Box>
        ) : null}
      </>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center" pr={1}>
        <DialogTitle style={{ paddingBottom: 0 }}>
          {step === 'warnings' ? 'Excluir categoria' : 'Confirmar exclusão'}
        </DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          {category?.name}
        </Typography>

        {usageLoading || !counts ? (
          <Box display="flex" justifyContent="center" py={4}>
            <LoadingProgress />
          </Box>
        ) : step === 'warnings' ? (
          renderWarningsStep()
        ) : (
          renderConfirmStep()
        )}
      </DialogContent>

      <DialogActions style={{ padding: '8px 24px 24px' }}>
        <Button
          onClick={step === 'confirm' ? () => setStep('warnings') : onClose}
          color="primary"
          style={{ textTransform: 'none' }}
          disabled={deleteLoading}>
          {step === 'confirm' ? 'Voltar' : 'Cancelar'}
        </Button>
        {step === 'warnings' ? (
          <PrimaryButton type="button" onClick={() => setStep('confirm')} disabled={!canContinue}>
            Continuar
          </PrimaryButton>
        ) : (
          <PrimaryButton type="button" onClick={handleConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Excluindo' : 'Excluir categoria'}
          </PrimaryButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

DeleteCategoryDialog.propTypes = {
  categories: PropTypes.array,
  category: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  deleteLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool,
  usage: PropTypes.shape({
    counts: PropTypes.object,
    exclusive: PropTypes.array,
    shared: PropTypes.array,
  }),
  usageLoading: PropTypes.bool,
};
